import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  ForgeDesignGroupDashboardRow,
  ForgeDesignGroupHierarchyLink,
  ForgeDesignGroupsChainSnapshot,
  ForgeDesignGroupScope,
  ForgeDesignGroupsDashboardResponse,
  ForgeDesignGroupSummary,
} from '../../types';

const PAGE_LIMIT = 50;
const SCOPE_OPTIONS: Array<{ value: ForgeDesignGroupScope | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'full', label: 'Full' },
  { value: 'partial', label: 'Partial' },
  { value: 'non-front', label: 'Non-front' },
];
const GROUP_SCOPE_OPTIONS = SCOPE_OPTIONS.filter(
  (option): option is { value: ForgeDesignGroupScope; label: string } => option.value !== 'all'
);

function formatNumber(value?: number | null): string {
  return (value ?? 0).toLocaleString();
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

function shortId(value: string): string {
  return value.length > 12 ? `${value.slice(0, 6)}...${value.slice(-4)}` : value;
}

function scopeLabel(scope: ForgeDesignGroupScope): string {
  if (scope === 'full') return 'Full';
  if (scope === 'partial') return 'Partial';
  return 'Non-front';
}

function scopeClass(scope: ForgeDesignGroupScope): string {
  if (scope === 'full') return 'is-full';
  if (scope === 'partial') return 'is-partial';
  return 'is-non-front';
}

function visibleRange(page: number, limit: number, total: number): string {
  if (total === 0) return '0';
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return `${formatNumber(start)}-${formatNumber(end)}`;
}

function totalGroups(chains: ForgeDesignGroupsChainSnapshot[]): number {
  return chains.reduce((total, chain) => total + chain.groupCount, 0);
}

function totalUniqueDesigns(chains: ForgeDesignGroupsChainSnapshot[]): number {
  return chains.reduce((total, chain) => total + chain.uniqueDesignCount, 0);
}

function strongestScopeCount(
  chains: ForgeDesignGroupsChainSnapshot[],
  scope: ForgeDesignGroupScope
): number {
  return chains.reduce((total, chain) => {
    if (scope === 'non-front') return total + chain.scopeTotals.nonFront;
    return total + chain.scopeTotals[scope];
  }, 0);
}

function mergeChainRefresh(
  current: ForgeDesignGroupsDashboardResponse,
  refreshed: ForgeDesignGroupsDashboardResponse,
  replaceGroups: boolean
): ForgeDesignGroupsDashboardResponse {
  const refreshedChainsById = new Map(
    refreshed.chains.map((chain) => [chain.chain.chainId, chain])
  );
  const currentChainIds = new Set(current.chains.map((chain) => chain.chain.chainId));
  const chains = [
    ...current.chains.map((chain) => refreshedChainsById.get(chain.chain.chainId) ?? chain),
    ...refreshed.chains.filter((chain) => !currentChainIds.has(chain.chain.chainId)),
  ].sort((left, right) => left.chain.chainId - right.chain.chainId);

  return {
    ...current,
    computedAtUtc: refreshed.computedAtUtc,
    supportedChainIds: refreshed.supportedChainIds,
    chains,
    groups: replaceGroups ? refreshed.groups : current.groups,
    total: replaceGroups ? refreshed.total : current.total,
    page: replaceGroups ? refreshed.page : current.page,
    limit: replaceGroups ? refreshed.limit : current.limit,
    hasNext: replaceGroups ? refreshed.hasNext : current.hasNext,
  };
}

interface ScopePillProps {
  scope: ForgeDesignGroupScope;
}

const ScopePill: React.FC<ScopePillProps> = ({ scope }) => (
  <span className={`dg-scope-pill ${scopeClass(scope)}`}>{scopeLabel(scope)}</span>
);

interface GroupThumbsProps {
  designs: ForgeDesignGroupSummary[];
  hiddenCount: number;
}

const GroupThumbs: React.FC<GroupThumbsProps> = ({ designs, hiddenCount }) => (
  <div className="dg-thumb-strip" aria-label="Design previews">
    {designs.slice(0, 8).map((design) => (
      <div
        key={design.designId}
        className="dg-thumb"
        title={design.normalizedBarcode ?? design.designId}
      >
        {design.previewVizUrl ? (
          <img src={design.previewVizUrl} alt="" loading="lazy" />
        ) : (
          <span>{shortId(design.designId)}</span>
        )}
      </div>
    ))}
    {hiddenCount > 0 ? (
      <div className="dg-thumb dg-thumb-more">+{formatNumber(hiddenCount)}</div>
    ) : null}
  </div>
);

interface ChainCardProps {
  chain: ForgeDesignGroupsChainSnapshot;
  isSelected: boolean;
  isRefreshing: boolean;
  onSelect: () => void;
  onRefresh: () => void;
}

const ChainCard: React.FC<ChainCardProps> = ({
  chain,
  isSelected,
  isRefreshing,
  onSelect,
  onRefresh,
}) => (
  <article className={`dg-chain-card ${isSelected ? 'is-selected' : ''}`}>
    <button type="button" className="dg-chain-select" onClick={onSelect}>
      <span className="dg-chain-title">{chain.chain.chainName}</span>
      <span className="dg-chain-subtitle">chain {chain.chain.chainId}</span>
      <span className="dg-chain-totals">
        <span className="dg-chain-stat">
          <strong>{formatNumber(chain.uniqueDesignCount)}</strong>
          designs
        </span>
        <span className="dg-chain-stat">
          <strong>{formatNumber(chain.groupCount)}</strong>
          groups
        </span>
      </span>
    </button>
    <div className="dg-chain-bars">
      <span
        className="dg-chain-bar is-full"
        style={{ '--value': chain.scopeTotals.full } as React.CSSProperties}
      >
        {formatNumber(chain.scopeTotals.full)}
      </span>
      <span
        className="dg-chain-bar is-partial"
        style={{ '--value': chain.scopeTotals.partial } as React.CSSProperties}
      >
        {formatNumber(chain.scopeTotals.partial)}
      </span>
      <span
        className="dg-chain-bar is-non-front"
        style={{ '--value': chain.scopeTotals.nonFront } as React.CSSProperties}
      >
        {formatNumber(chain.scopeTotals.nonFront)}
      </span>
    </div>
    <button
      type="button"
      className="dg-icon-button"
      aria-label={`Refresh ${chain.chain.chainName}`}
      title={`Refresh ${chain.chain.chainName}`}
      disabled={isRefreshing}
      onClick={onRefresh}
    >
      <RefreshOutlinedIcon className={isRefreshing ? 'is-spinning' : ''} />
    </button>
  </article>
);

interface GroupRowProps {
  row: ForgeDesignGroupDashboardRow;
  isSelected: boolean;
  isTypeMenuOpen: boolean;
  isChangingType: boolean;
  onSelect: () => void;
  onOpenTypeMenu: () => void;
  onChangeScope: (scope: ForgeDesignGroupScope) => void;
}

interface ScopeChangeNotice {
  fromScope: ForgeDesignGroupScope;
  toScope: ForgeDesignGroupScope;
}

const GroupRow: React.FC<GroupRowProps> = ({
  row,
  isSelected,
  isTypeMenuOpen,
  isChangingType,
  onSelect,
  onOpenTypeMenu,
  onChangeScope,
}) => (
  <tr
    className={`dg-group-row ${isSelected ? 'is-selected' : ''}`}
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect();
      }
    }}
  >
    <td className="dg-row-type">
      <ScopePill scope={row.scope} />
    </td>
    <td className="dg-row-count">{formatNumber(row.designCount)} designs</td>
    <td className="dg-row-examples">
      <GroupThumbs designs={row.designs} hiddenCount={row.hiddenDesignCount} />
    </td>
    <td className="dg-row-hierarchy">
      {row.parentGroups.length + row.childGroups.length > 0
        ? `${row.parentGroups.length} up / ${row.childGroups.length} down`
        : 'No linked scopes'}
    </td>
    <td className="dg-row-timestamp">{formatTimestamp(row.createdAt)}</td>
    <td className="dg-row-timestamp">{formatTimestamp(row.updatedAt)}</td>
    <td className="dg-row-user" title={row.updatedBy ?? undefined}>
      {row.updatedBy ?? '-'}
    </td>
    <td className="dg-row-actions-cell">
      <div className="dg-group-row-actions">
        {row.masterLabelUrl ? (
          <Link
            className="dg-text-link"
            to={row.masterLabelUrl}
            onClick={(event) => event.stopPropagation()}
          >
            <OpenInNewOutlinedIcon />
            Master Label
          </Link>
        ) : null}
        <button
          type="button"
          className="dg-type-button"
          aria-expanded={isTypeMenuOpen}
          aria-label={`Change group type for ${shortId(row.groupId)}`}
          disabled={isChangingType}
          onClick={(event) => {
            event.stopPropagation();
            onOpenTypeMenu();
          }}
        >
          <TuneOutlinedIcon className={isChangingType ? 'is-spinning' : ''} />
          Type
        </button>
        {isTypeMenuOpen ? (
          <div
            className="dg-type-menu"
            role="menu"
            aria-label={`Group type options for ${shortId(row.groupId)}`}
          >
            {GROUP_SCOPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="menuitem"
                disabled={row.scope === option.value || isChangingType}
                onClick={(event) => {
                  event.stopPropagation();
                  onChangeScope(option.value);
                }}
              >
                <ScopePill scope={option.value} />
                {row.scope === option.value ? 'Current' : `Make ${option.label}`}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </td>
  </tr>
);

interface HierarchyLinkButtonProps {
  link: ForgeDesignGroupHierarchyLink;
  onOpen: (groupId: string) => void;
}

const HierarchyLinkButton: React.FC<HierarchyLinkButtonProps> = ({ link, onOpen }) => (
  <button type="button" className="dg-tree-node" onClick={() => onOpen(link.groupId)}>
    <ScopePill scope={link.scope} />
    <span>{scopeLabel(link.scope)} group</span>
    <strong>{formatNumber(link.designCount)}</strong>
    <em>{formatNumber(link.sharedDesignCount)} shared</em>
  </button>
);

interface HierarchyPanelProps {
  row: ForgeDesignGroupDashboardRow | null;
  onOpenGroup: (groupId: string, chainId: number) => void;
}

const HierarchyPanel: React.FC<HierarchyPanelProps> = ({ row, onOpenGroup }) => {
  if (!row) {
    return (
      <aside className="forge-card dg-hierarchy-panel">
        <div className="dg-panel-title">
          <AccountTreeOutlinedIcon />
          Hierarchy
        </div>
        <div className="dg-empty-state">No group selected.</div>
      </aside>
    );
  }

  return (
    <aside className="forge-card dg-hierarchy-panel">
      <div className="dg-panel-title">
        <AccountTreeOutlinedIcon />
        <span className="dg-panel-title-text" title={`Hierarchy for ${row.groupId}`}>
          Hierarchy for {row.groupId}
        </span>
      </div>
      <div className="dg-selected-summary">
        <ScopePill scope={row.scope} />
        <span>{formatNumber(row.designCount)} designs</span>
      </div>
      <div className="dg-tree">
        <div className="dg-tree-section">
          <div className="dg-tree-label">Stronger</div>
          {row.parentGroups.length > 0 ? (
            row.parentGroups.map((link) => (
              <HierarchyLinkButton
                key={link.groupId}
                link={link}
                onOpen={(groupId) => onOpenGroup(groupId, row.chainId)}
              />
            ))
          ) : (
            <div className="dg-tree-empty">None</div>
          )}
        </div>
        <div className="dg-tree-current">
          <ScopePill scope={row.scope} />
          <span>Selected group</span>
        </div>
        <div className="dg-tree-section">
          <div className="dg-tree-label">Weaker</div>
          {row.childGroups.length > 0 ? (
            row.childGroups.map((link) => (
              <HierarchyLinkButton
                key={link.groupId}
                link={link}
                onOpen={(groupId) => onOpenGroup(groupId, row.chainId)}
              />
            ))
          ) : (
            <div className="dg-tree-empty">None</div>
          )}
        </div>
      </div>
      <div className="dg-panel-title dg-panel-title--secondary">Members</div>
      <div className="dg-member-list">
        {row.designs.map((design) => (
          <Link
            key={design.designId}
            className="dg-member"
            to={design.forgeUrl ?? row.masterLabelUrl ?? '#'}
          >
            {design.previewVizUrl ? <img src={design.previewVizUrl} alt="" loading="lazy" /> : null}
            <span>
              <strong>{design.normalizedBarcode ?? shortId(design.designId)}</strong>
              <em>{shortId(design.designId)}</em>
            </span>
          </Link>
        ))}
        {row.hiddenDesignCount > 0 ? (
          <div className="dg-member-overflow">
            +{formatNumber(row.hiddenDesignCount)} more designs
          </div>
        ) : null}
      </div>
    </aside>
  );
};

export const DesignGroupsDashboard: React.FC = () => {
  const { token } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [dashboard, setDashboard] = useState<ForgeDesignGroupsDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedChainId, setSelectedChainId] = useState<number | 'all'>('all');
  const [selectedScope, setSelectedScope] = useState<ForgeDesignGroupScope | 'all'>('all');
  const [queryInput, setQueryInput] = useState('');
  const [committedQuery, setCommittedQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [refreshingChainIds, setRefreshingChainIds] = useState<Set<number>>(() => new Set());
  const [typeMenuGroupId, setTypeMenuGroupId] = useState<string | null>(null);
  const [changingTypeGroupId, setChangingTypeGroupId] = useState<string | null>(null);
  const [scopeChangeNotice, setScopeChangeNotice] = useState<ScopeChangeNotice | null>(null);
  const loadRequestSequence = useRef(0);

  const loadDashboard = useCallback(async () => {
    const requestSequence = loadRequestSequence.current + 1;
    loadRequestSequence.current = requestSequence;
    setIsLoading(true);
    try {
      const nextDashboard = await ForgeApiService.fetchDesignGroupsDashboard(token, {
        chainId: selectedChainId === 'all' ? null : selectedChainId,
        scope: selectedScope === 'all' ? null : selectedScope,
        query: committedQuery || null,
        page,
        limit: PAGE_LIMIT,
      });
      if (loadRequestSequence.current !== requestSequence) return;
      setDashboard(nextDashboard);
      setLoadError(null);
    } catch (error) {
      if (loadRequestSequence.current !== requestSequence) return;
      const message = error instanceof Error ? error.message : 'Error loading Design Groups';
      setDashboard(null);
      setLoadError(message);
      showErrorToast(message);
    } finally {
      if (loadRequestSequence.current === requestSequence) {
        setIsLoading(false);
      }
    }
  }, [committedQuery, page, selectedChainId, selectedScope, showErrorToast, token]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!dashboard?.groups.length) {
      setSelectedGroupId(null);
      setTypeMenuGroupId(null);
      return;
    }
    if (!selectedGroupId || !dashboard.groups.some((group) => group.groupId === selectedGroupId)) {
      setSelectedGroupId(dashboard.groups[0].groupId);
    }
    if (typeMenuGroupId && !dashboard.groups.some((group) => group.groupId === typeMenuGroupId)) {
      setTypeMenuGroupId(null);
    }
  }, [dashboard?.groups, selectedGroupId, typeMenuGroupId]);

  const selectedGroup = useMemo(
    () => dashboard?.groups.find((group) => group.groupId === selectedGroupId) ?? null,
    [dashboard?.groups, selectedGroupId]
  );

  const chainOptions = useMemo(() => dashboard?.chains ?? [], [dashboard?.chains]);
  const isRefreshingAnyChain = refreshingChainIds.size > 0;

  const refreshRequest = (chainId: number) => ({
    chain_id: chainId,
    scope: selectedScope === 'all' ? null : selectedScope,
    query: committedQuery || null,
    page,
    limit: PAGE_LIMIT,
  });

  const dashboardSliceRequest = () => ({
    dashboard_chain_id: selectedChainId === 'all' ? null : selectedChainId,
    dashboard_scope: selectedScope === 'all' ? null : selectedScope,
    query: committedQuery || null,
    page,
    limit: PAGE_LIMIT,
  });

  const handleRefreshChain = async (chainId: number) => {
    setRefreshingChainIds((current) => new Set(current).add(chainId));
    try {
      const refreshed = await ForgeApiService.refreshDesignGroupsDashboard(
        token,
        refreshRequest(chainId)
      );
      setDashboard((current) =>
        current ? mergeChainRefresh(current, refreshed, selectedChainId === chainId) : refreshed
      );
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error refreshing Design Groups');
    } finally {
      setRefreshingChainIds((current) => {
        const next = new Set(current);
        next.delete(chainId);
        return next;
      });
    }
  };

  const handleChangeGroupScope = async (
    row: ForgeDesignGroupDashboardRow,
    nextScope: ForgeDesignGroupScope
  ) => {
    if (row.scope === nextScope) {
      setTypeMenuGroupId(null);
      return;
    }
    setChangingTypeGroupId(row.groupId);
    try {
      const refreshed = await ForgeApiService.updateDesignGroupScope(token, row.groupId, {
        scope: nextScope,
        ...dashboardSliceRequest(),
      });
      setDashboard(refreshed);
      const changedGroupStillVisible = refreshed.groups.some(
        (group) => group.groupId === row.groupId
      );
      setSelectedGroupId(
        changedGroupStillVisible ? row.groupId : (refreshed.groups[0]?.groupId ?? null)
      );
      setTypeMenuGroupId(null);
      setScopeChangeNotice(
        !changedGroupStillVisible && selectedScope !== 'all' && selectedScope !== nextScope
          ? { fromScope: row.scope, toScope: nextScope }
          : null
      );
      showSuccessToast(`Group changed to ${scopeLabel(nextScope)}`);
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error changing Design Group type');
    } finally {
      setChangingTypeGroupId(null);
    }
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setScopeChangeNotice(null);
    setCommittedQuery(queryInput.trim());
  };

  const handleOpenHierarchyGroup = (groupId: string, chainId: number) => {
    setSelectedChainId(chainId);
    setSelectedScope('all');
    setQueryInput(groupId);
    setCommittedQuery(groupId);
    setPage(1);
  };

  const handleSelectChain = (chainId: number | 'all') => {
    setSelectedChainId(chainId);
    setTypeMenuGroupId(null);
    setScopeChangeNotice(null);
    setPage(1);
  };

  const handleSelectScope = (scope: ForgeDesignGroupScope | 'all') => {
    setSelectedScope(scope);
    setTypeMenuGroupId(null);
    setScopeChangeNotice(null);
    setPage(1);
  };

  const clearSearch = () => {
    setQueryInput('');
    setCommittedQuery('');
    setTypeMenuGroupId(null);
    setScopeChangeNotice(null);
    setPage(1);
  };

  return (
    <div className="page-content page-content--wide dg-dashboard-page">
      <section className="forge-card dg-dashboard-hero">
        <div className="dg-hero-main">
          <div>
            <div className="dg-dashboard-title">Design Groups</div>
            <div className="dg-hero-metrics">
              <span>
                <strong>{formatNumber(totalUniqueDesigns(dashboard?.chains ?? []))}</strong>
                designs
              </span>
              <span>
                <strong>{formatNumber(totalGroups(dashboard?.chains ?? []))}</strong>
                groups
              </span>
              <span>
                <strong>
                  {formatNumber(strongestScopeCount(dashboard?.chains ?? [], 'full'))}
                </strong>
                full
              </span>
              <span>
                <strong>
                  {formatNumber(strongestScopeCount(dashboard?.chains ?? [], 'partial'))}
                </strong>
                partial
              </span>
              <span>
                <strong>
                  {formatNumber(strongestScopeCount(dashboard?.chains ?? [], 'non-front'))}
                </strong>
                non-front
              </span>
            </div>
          </div>
          <form className="dg-search" onSubmit={handleSearchSubmit}>
            <SearchOutlinedIcon />
            <input
              type="search"
              aria-label="Search Design Groups"
              placeholder="Group ID, design ID, or barcode"
              value={queryInput}
              onChange={(event) => setQueryInput(event.target.value)}
            />
            {committedQuery ? (
              <button type="button" className="dg-search-clear" onClick={clearSearch}>
                Clear
              </button>
            ) : null}
            <button type="submit" className="dg-primary-button">
              Search
            </button>
          </form>
        </div>
        <div className="dg-filter-row">
          <div className="dg-filter-group">
            <FilterListOutlinedIcon />
            <select
              aria-label="Design Groups chain filter"
              value={selectedChainId}
              onChange={(event) =>
                handleSelectChain(event.target.value === 'all' ? 'all' : Number(event.target.value))
              }
            >
              <option value="all">All supported chains</option>
              {chainOptions.map((chain) => (
                <option key={chain.chain.chainId} value={chain.chain.chainId}>
                  {chain.chain.chainName}
                </option>
              ))}
            </select>
          </div>
          <div className="dg-scope-tabs" role="group" aria-label="Design group scope">
            {SCOPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={selectedScope === option.value ? 'is-active' : ''}
                onClick={() => handleSelectScope(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="dg-secondary-button"
            disabled={isLoading || isRefreshingAnyChain}
            onClick={() => void loadDashboard()}
          >
            <RefreshOutlinedIcon className={isLoading ? 'is-spinning' : ''} />
            Refresh view
          </button>
        </div>
      </section>

      <section className="dg-chain-grid" aria-label="Design Groups chain stats">
        {chainOptions.map((chain) => (
          <ChainCard
            key={chain.chain.chainId}
            chain={chain}
            isSelected={selectedChainId === chain.chain.chainId}
            isRefreshing={refreshingChainIds.has(chain.chain.chainId)}
            onSelect={() => handleSelectChain(chain.chain.chainId)}
            onRefresh={() => void handleRefreshChain(chain.chain.chainId)}
          />
        ))}
      </section>

      <section className="dg-dashboard-grid">
        <div className="forge-card dg-list-panel">
          <div className="dg-list-header">
            <div>
              <div className="dg-panel-title">Groups</div>
              <div className="dg-list-count">
                Showing{' '}
                {visibleRange(
                  dashboard?.page ?? page,
                  dashboard?.limit ?? PAGE_LIMIT,
                  dashboard?.total ?? 0
                )}{' '}
                of {formatNumber(dashboard?.total ?? 0)}
              </div>
            </div>
            <div className="dg-pagination">
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Previous page"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <ChevronLeftOutlinedIcon />
              </button>
              <span>Page {formatNumber(page)}</span>
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Next page"
                disabled={!dashboard?.hasNext || isLoading}
                onClick={() => setPage((current) => current + 1)}
              >
                <ChevronRightOutlinedIcon />
              </button>
            </div>
          </div>
          {scopeChangeNotice ? (
            <div className="dg-scope-change-notice" role="status">
              <span>
                Changed from {scopeLabel(scopeChangeNotice.fromScope)} to{' '}
                {scopeLabel(scopeChangeNotice.toScope)}. It is hidden by the current{' '}
                {scopeLabel(scopeChangeNotice.fromScope)} filter.
              </span>
              <button
                type="button"
                className="dg-notice-button"
                onClick={() => handleSelectScope(scopeChangeNotice.toScope)}
              >
                Show {scopeLabel(scopeChangeNotice.toScope)}
              </button>
              <button
                type="button"
                className="dg-notice-dismiss"
                aria-label="Dismiss group type change notice"
                onClick={() => setScopeChangeNotice(null)}
              >
                Dismiss
              </button>
            </div>
          ) : null}
          <div className="dg-group-list">
            {isLoading ? (
              <div className="dg-loading-state">Loading Design Groups...</div>
            ) : loadError ? (
              <div className="dg-empty-state">
                <strong>Design Groups could not be loaded</strong>
                <span>{loadError}</span>
                <button
                  type="button"
                  className="dg-secondary-button"
                  onClick={() => void loadDashboard()}
                >
                  Retry
                </button>
              </div>
            ) : dashboard?.groups.length ? (
              <div className="dg-group-table-scroll">
                <table className="dg-group-table" aria-label="Design Groups table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Designs</th>
                      <th>Examples</th>
                      <th>Hierarchy</th>
                      <th>Created</th>
                      <th>Updated</th>
                      <th>Updated by</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.groups.map((row) => (
                      <GroupRow
                        key={row.groupId}
                        row={row}
                        isSelected={row.groupId === selectedGroupId}
                        isTypeMenuOpen={row.groupId === typeMenuGroupId}
                        isChangingType={row.groupId === changingTypeGroupId}
                        onSelect={() => setSelectedGroupId(row.groupId)}
                        onOpenTypeMenu={() =>
                          setTypeMenuGroupId((current) =>
                            current === row.groupId ? null : row.groupId
                          )
                        }
                        onChangeScope={(scope) => void handleChangeGroupScope(row, scope)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="dg-empty-state">No Design Groups match this view.</div>
            )}
          </div>
        </div>
        <HierarchyPanel row={selectedGroup} onOpenGroup={handleOpenHierarchyGroup} />
      </section>
    </div>
  );
};
